import type { ActivationLocationType } from '../redux/ownerActivationSlice';

/**
 * Pure completed-sale evidence resolver (US5, T117; activation-events contract
 * §SALE_COMPLETED).
 *
 * Only a *terminal* real-location order success is First-sale evidence. Test,
 * pending, cancelled, partial-unfinished, and unknown shapes resolve to null.
 * A terminal test-location sale returns test evidence, which callers may send
 * to practice analytics but which never completes First sale.
 */

export type CheckoutFlow = 'standard' | 'partial';

export interface CheckoutCompletionCandidate {
  flow: CheckoutFlow;
  responseStatus: unknown;
  paymentStatus: unknown;
  /** Order lines from the response; only inspected for partial checkout. */
  orderItems?: Array<{ bp_order_completed?: unknown }>;
  locationId: string | null;
  locationType: ActivationLocationType;
}

export interface CompletedSaleEvidence {
  locationId: string | null;
  locationType: ActivationLocationType;
}

const isTrue = (value: unknown): boolean => value === true;

/**
 * Returns sale evidence when the candidate is a terminal payment success, or
 * null otherwise. Location type is preserved so callers distinguish real
 * (First-sale) from test (practice-only) sales.
 */
export const resolveCompletedSaleEvidence = (
  candidate: CheckoutCompletionCandidate | null | undefined,
): CompletedSaleEvidence | null => {
  if (!candidate) return null;

  // 1 & 2: terminal payment gate shared by both flows.
  if (candidate.responseStatus !== 200) return null;
  if (!isTrue(candidate.paymentStatus)) return null;

  if (candidate.flow === 'standard') {
    // 3: standard checkout success is terminal.
    return {
      locationId: candidate.locationId ?? null,
      locationType: candidate.locationType,
    };
  }

  if (candidate.flow === 'partial') {
    const items = candidate.orderItems;
    // 4 & 5: partial requires explicit all-line completion; missing evidence
    // defers to a subsequent verified completed-order bootstrap.
    if (!Array.isArray(items) || items.length === 0) return null;
    const everyLineCompleted = items.every(item =>
      isTrue(item?.bp_order_completed),
    );
    if (!everyLineCompleted) return null;
    return {
      locationId: candidate.locationId ?? null,
      locationType: candidate.locationType,
    };
  }

  // 6: unknown flow shape.
  return null;
};

export default resolveCompletedSaleEvidence;
